import { Table, Select, Button, Modal, Input, Tag, message } from "antd";
import { useState, useContext, useEffect } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

export default function GetStatusChange() {
    const { authData, backUrl } = useContext(AuthContext);
    const [statusChanges, setStatusChanges] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [filters, setFilters] = useState({ status: null, year: null });

    const [isApproveModalVisible, setIsApproveModalVisible] = useState(false);
    const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
    const [adminRemarks, setAdminRemarks] = useState("");
    const [selectedRecord, setSelectedRecord] = useState(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // ✅ Fetch status change requests
    const fetchData = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/employee-status-change/`, {
                headers: { Authorization: `Bearer ${authData.access}` },
            });
            setStatusChanges(res.data.results || res.data || []);
        } catch (error) {
            console.error("Error fetching status changes:", error.response || error.message);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // ✅ Status colors
    const statusColors = {
        Pending: "orange",
        Approved: "green",
        Rejected: "red",
        Cancelled: "gray",
        RESIGNED: "volcano",
        TERMINATED: "red",
        RETIRED: "purple",
        ABSCONDED: "magenta",
    };

    // ✅ Handle Approve
    const handleApprove = async () => {
        if (!selectedRecord?.id) return;
        try {
            const res = await axios.post(
                `${backUrl}/api/emp/employee-status-change/${selectedRecord.id}/approve/`,
                { admin_remarks: adminRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success(res.data?.message || "Status change approved successfully.");
            setIsApproveModalVisible(false);
            setAdminRemarks("");
            setSelectedRecord(null);
            await fetchData();
        } catch (error) {
            console.error("Error approving status change:", error);
            message.error(error.response?.data?.message || "Failed to approve status change.");
        }
    };

    // ✅ Handle Reject
    const handleReject = async () => {
        if (!selectedRecord?.id) return;
        try {
            const res = await axios.post(
                `${backUrl}/api/emp/employee-status-change/${selectedRecord.id}/reject/`,
                { admin_remarks: adminRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success(res.data?.message || "Status change rejected successfully.");
            setIsRejectModalVisible(false);
            setAdminRemarks("");
            setSelectedRecord(null);
            await fetchData();
        } catch (error) {
            console.error("Error rejecting status change:", error);
            message.error(error.response?.data?.message || "Failed to reject status change.");
        }
    };

    // ✅ Columns
    const columns = [
        {
            title: "Employee",
            dataIndex: "employee",
            key: "employee",
            render: (val) => {
                if (!val) return "-";
                if (typeof val === "object") {
                    const user = val.user;
                    if (user) {
                        return `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username || "-";
                    }
                    return val.username || "-";
                }
                return val;
            },
        },
        {
            title: "Requested Status",
            dataIndex: "requested_status",
            key: "requested_status",
            render: (text) => (
                <Tag color={statusColors[text] || "blue"}>{text || "-"}</Tag>
            ),
        },
        {
            title: "Date of Leaving",
            dataIndex: "date_of_leaving",
            key: "date_of_leaving",
            render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Last Working Day",
            dataIndex: "last_working_day",
            key: "last_working_day",
            render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Probation End Date",
            dataIndex: "probation_end_date",
            key: "probation_end_date",
            render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Confirmation Date",
            dataIndex: "employee_confirmation_date",
            key: "employee_confirmation_date",
            render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Remarks",
            dataIndex: "remarks",
            key: "remarks",
            width: 300,
            render: (text) => (
                <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "300px" }}>
                    {text || "-"}
                </div>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text) => (
                <Tag color={statusColors[text] || "default"}>{text || "-"}</Tag>
            ),
        },
        {
            title: "Admin Remarks",
            dataIndex: "admin_remarks",
            key: "admin_remarks",
            width: 300,
            render: (text) => (
                <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "300px" }}>
                    {text || "-"}
                </div>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => {
                if (record.status === "Approved" || record.status === "Rejected") return "-";
                return (
                    <div style={{ display: "flex", gap: "8px" }}>
                        <Button
                            type="text"
                            style={{ color: "green" }}
                            icon={<CheckOutlined />}
                            onClick={() => {
                                setSelectedRecord(record);
                                setIsApproveModalVisible(true);
                            }}
                        />
                        <Button
                            type="text"
                            danger
                            icon={<CloseOutlined />}
                            onClick={() => {
                                setSelectedRecord(record);
                                setIsRejectModalVisible(true);
                            }}
                        />
                    </div>
                );
            },
        },
    ];

    // ✅ Filter logic — use date_of_leaving for year extraction
    const years = Array.from(
        new Set(
            statusChanges
                .filter((item) => item.date_of_leaving || item.last_working_day)
                .map((item) => dayjs(item.date_of_leaving || item.last_working_day).year())
        )
    ).sort((a, b) => b - a);

    const filteredData = statusChanges.filter((item) => {
        const date = dayjs(item.date_of_leaving || item.last_working_day);
        const matchStatus = filters.status ? item.status === filters.status : true;
        const matchYear = filters.year ? date.year() === filters.year : true;
        return matchStatus && matchYear;
    });

    return (
        <>
            {/* ✅ Approve Modal */}
            <Modal
                title="Approve Status Change"
                open={isApproveModalVisible}
                onOk={handleApprove}
                onCancel={() => {
                    setIsApproveModalVisible(false);
                    setAdminRemarks("");
                    setSelectedRecord(null);
                }}
                okText="Approve"
                cancelText="Close"
                okButtonProps={{ style: { backgroundColor: "green", borderColor: "green" } }}
            >
                <p>
                    Are you sure you want to <strong>approve</strong> this status change request
                    {selectedRecord?.id ? ` (ID: ${selectedRecord.id})` : ""}?
                </p>
                <Input.TextArea
                    rows={4}
                    placeholder="Enter admin remarks"
                    value={adminRemarks}
                    onChange={(e) => setAdminRemarks(e.target.value)}
                />
            </Modal>

            {/* ✅ Reject Modal */}
            <Modal
                title="Reject Status Change"
                open={isRejectModalVisible}
                onOk={handleReject}
                onCancel={() => {
                    setIsRejectModalVisible(false);
                    setAdminRemarks("");
                    setSelectedRecord(null);
                }}
                okText="Reject"
                cancelText="Close"
                okButtonProps={{ danger: true }}
            >
                <p>
                    Are you sure you want to <strong>reject</strong> this status change request
                    {selectedRecord?.id ? ` (ID: ${selectedRecord.id})` : ""}?
                </p>
                <Input.TextArea
                    rows={4}
                    placeholder="Enter rejection remarks"
                    value={adminRemarks}
                    onChange={(e) => setAdminRemarks(e.target.value)}
                />
            </Modal>

            {/* ✅ Filters */}
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "10px",
                    marginBottom: "16px",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: "center",
                }}
            >
                <Select
                    placeholder="Filter by Year"
                    style={{ width: isMobile ? "100%" : 140 }}
                    allowClear
                    onChange={(value) => setFilters((prev) => ({ ...prev, year: value }))}
                >
                    {years.map((year) => (
                        <Select.Option key={year} value={year}>
                            {year}
                        </Select.Option>
                    ))}
                </Select>

                <Select
                    placeholder="Filter by Status"
                    style={{ width: isMobile ? "100%" : 200 }}
                    allowClear
                    onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
                >
                    {["Pending", "Approved", "Rejected", "Cancelled"].map((status) => (
                        <Select.Option key={status} value={status}>
                            {status}
                        </Select.Option>
                    ))}
                </Select>
            </div>

            {/* ✅ Table */}
            <Table
                columns={columns}
                dataSource={filteredData.map((item, index) => ({
                    key: index,
                    ...item,
                }))}
                pagination={{ pageSize: 5 }}
                scroll={{ x: true }}
                components={{
                    header: {
                        cell: (props) => (
                            <th
                                {...props}
                                style={{
                                    backgroundColor: "#0e497b",
                                    color: "white",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {props.children}
                            </th>
                        ),
                    },
                }}
                bordered
            />
        </>
    );
}